import React, { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { Grid, Table, TableBody, TableCell, TableHead, TableRow, CircularProgress } from '@mui/material';
import { IconButton, Typography } from '@totalsoft/rocket-ui';
import CheckIcon from '@mui/icons-material/Check';
import ErrorIcon from '@mui/icons-material/Error';
import AddBoxIcon from '@mui/icons-material/AddBox';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import AddFinancialProductToolbarComponent from './Toolbar';
import { addNewFinancialProduct } from '../../actionCreators';
import WrappedFakeText from 'components/common/WrappedFakeText';

const FinancialProductListComponent = ({
  availableFinancialProducts,
  onFilterValueChanged,
  fullTextFilter,
  onResetFilters,
  documentId,
  siteId,
  onProductSelection,
  onProductSelected,
  loading,
  loaded,
  disabledStatus
}) => {
  const [t] = useTranslation();

  const dispatch = useDispatch();

  const onFinancialProductSet = (documentId, siteId, financialProductId) => {
    onProductSelection(financialProductId);
    dispatch(addNewFinancialProduct(documentId, siteId, financialProductId, hasError => onProductSelected(financialProductId, hasError)));
  };

  const getDisabledItemStatus = useCallback(
    financialProductId =>
      !!availableFinancialProducts.find(
        fp => !fp.selecting && fp.id === financialProductId && availableFinancialProducts.some(item => item.selecting || item.selected)
      ),
    [availableFinancialProducts]
  );

  return (
    <>
      <Grid item xs={12}>
        <Grid container spacing={1} alignItems='center'>
          <Grid item xs={6}>
            <Typography variant='h6'>{t('OfferEdit.FinancialProducts.FinancialProductAddModal.Title')}</Typography>
          </Grid>
          <Grid item xs={6}>
            <AddFinancialProductToolbarComponent
              onFilterValueChanged={onFilterValueChanged}
              fullTextFilter={fullTextFilter}
              onResetFilters={onResetFilters}
              disabledStatus={disabledStatus}
            />
          </Grid>
        </Grid>
        <Grid item xs={12}>
          {loading && <WrappedFakeText lines={6} />}
          {loaded && (
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>
                    <strong>{t('OfferEdit.FinancialProducts.FinancialProductAddModal.Name')}</strong>
                  </TableCell>
                  <TableCell>
                    <strong>{t('OfferEdit.FinancialProducts.FinancialProductAddModal.ContractTypeName')}</strong>
                  </TableCell>
                  <TableCell>
                    <strong>{t('OfferEdit.FinancialProducts.FinancialProductAddModal.Currency')}</strong>
                  </TableCell>
                  <TableCell></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {availableFinancialProducts.map(elem => {
                  return (
                    <TableRow key={elem.id}>
                      <TableCell>{elem.name}</TableCell>
                      <TableCell>{elem.contractTypeName}</TableCell>
                      <TableCell>{elem.currencyCode}</TableCell>
                      <TableCell sx={{ textAlign: 'end' }} onClick={() => onFinancialProductSet(documentId, siteId, elem.id)}>
                        {elem.selecting ? (
                          <CircularProgress />
                        ) : elem.selected ? (
                          <CheckIcon color='success' />
                        ) : elem.hasError ? (
                          <ErrorIcon color='error' />
                        ) : (
                          <IconButton
                            color='primary'
                            variant='text'
                            size='small'
                            tooltip={t('Add')}
                            disabled={getDisabledItemStatus(elem.id)}
                          >
                            <AddBoxIcon />
                          </IconButton>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </Grid>
      </Grid>
    </>
  );
};

FinancialProductListComponent.propTypes = {
  availableFinancialProducts: PropTypes.array.isRequired,
  onFilterValueChanged: PropTypes.func.isRequired,
  fullTextFilter: PropTypes.string.isRequired,
  onResetFilters: PropTypes.func.isRequired,
  onProductSelection: PropTypes.func.isRequired,
  onProductSelected: PropTypes.func.isRequired,
  documentId: PropTypes.number.isRequired,
  siteId: PropTypes.number.isRequired,
  loading: PropTypes.bool.isRequired,
  loaded: PropTypes.bool.isRequired,
  disabledStatus: PropTypes.bool.isRequired
};

export default FinancialProductListComponent;
